import * as React from 'react';
import DataGrid from 'devextreme-react/data-grid';
import { withStyles } from '@material-ui/core/styles';
import { IDiaryGridProps } from './DiaryGrid.properties';
import styles from './DiaryGrid.styles';
import { createDataSource, useGridKeyboardNavigation } from 'components/common/DataGridDevEx/DataGrid.hooks';
import { Paper } from '@material-ui/core';
import FilterRow from 'components/common/DataGridDevEx/FilterRow';
import filterRow from './DiaryGrid.filterRow';
import DynamicDataGrid from 'components/common/DataGridDevEx/DynamicDataGrid';
import { columns } from './constants';
import { IDiaryFacade } from 'api/swaggerTypes';
import { useRetrieveDiaryLines, useUpdateDiary, useDeleteDiaryLines } from 'api/common/diary/diary';

function DiaryGrid(props: Readonly<IDiaryGridProps>): JSX.Element {
    const { classes, showDetailOnClick, changeConfirmationDialog, formValues, module } = props;
    const dataGridInnerRef = React.useRef<DataGrid>();
    const filterButtonRef = React.useRef<HTMLButtonElement>();

    const { onFocusedCellChanging } = useGridKeyboardNavigation();
    const fetchLinesMutation = useRetrieveDiaryLines(module);
    const updateLinesMutation = useUpdateDiary(module);
    const deleteLinesMutation = useDeleteDiaryLines(module);

    const [appliedFilters, setAppliedFilters] = React.useState(formValues);

    const isDisabled = React.useCallback(
        (entry) => {
            if (module?.name === 'PurchaseOrders') {

                return entry.SystemGenerated || module?.summaryDetails?.Status?.toLowerCase() === 'complete';
            }

            if (module?.name === 'PickSlips') {

                return entry.SystemGenerated;
            }

            return false;
        },
        [module?.name, module?.summaryDetails?.Status]
    );

    const fetchLines = React.useCallback(
        (BatchPage, BatchSize, Sort) => module?.selectedItem ? fetchLinesMutation.mutateAsync({
            [module?.entity]: module?.selectedItem,
            ...appliedFilters,
            urlQueryParams: {
                BatchPage,
                BatchSize,
                Sort,
            }
        }).then((response) => {
            response.DiaryEntry = response?.DiaryEntry?.map((entry) => ({
                ...entry,
                DisableLine: entry.LineType === 'r',
                DisableEditAction: isDisabled(entry),
                DisableDeleteAction: isDisabled(entry)
            }));

            return response?.DiaryEntry;
        }) : ([] as any),
        [module?.selectedItem, appliedFilters, isDisabled]
    );

    const updateLine = React.useCallback(
        async (data: any) => {
            const item = dataGridInnerRef.current?.instance?.getDataSource().items().find((f) => f.Id === data.Id);
            const result = await updateLinesMutation.mutateAsync({ ...item, DiaryId: item.DiaryId, Subject: item.Subject, CommentFull: item.CommentFull, ...data });

            if (result.Status && !result.Forms) {
                reloadLines();
            }

            return result;
        },
        []
    );

    const diaryDataSource = React.useMemo(
        () => createDataSource(
            'Id',
            { fetch: fetchLines, update: updateLine }
        ),
        [fetchLines, updateLine]
    );

    const reloadLines = async () => {
        await diaryDataSource.reload();
        await dataGridInnerRef.current?.instance?.refresh(true);
    };

    async function deleteClick(_: string, row: IDiaryFacade): Promise<void> {
        changeConfirmationDialog({
            open: true,
            title: 'Delete selected record',
            message: 'Are you sure you want to delete the selected record?',
            okLabel: 'Delete',
            onCancel: () => undefined,
            onOk: async () => {
                const { Status } = await deleteLinesMutation.mutateAsync({
                    DiaryId: row.DiaryId
                });
                if (Status) {
                    const dataSource = dataGridInnerRef.current?.instance.getDataSource();
                    const currentItems = dataSource.items();
                    const updatedItems = currentItems.filter((item) => item.DiaryId !== row.DiaryId);
                    dataGridInnerRef.current?.instance.option('dataSource', updatedItems);
                    await reloadLines();
                }

                return Status;
            }
        });
    }

    const detailOnClick = (allProps) => {
        const disableLine = allProps.data && allProps.data.LineType === 'r';

        if (!disableLine && showDetailOnClick) {
            showDetailOnClick(allProps.data ? allProps.data : null);
        }
    };

    const onApplyFilters = () => {
        setAppliedFilters(formValues);
    };

    return (
        <Paper className={classes.paperContainer}>
            <FilterRow
                filterButtonRef={filterButtonRef}
                disableInitialApplyCall={true}
                parameters={filterRow.parameters}
                formName={filterRow.formName}
                onApplyFilters={onApplyFilters}
                initialValues={{}}
                valuesSchema={{}}
                validate={filterRow.validate}
                rowModelType={filterRow.rowModelType}
                onFilterButtonShiftTab={() => { return; }}
            />
            <DynamicDataGrid
                dataGridClassName={classes.commitmentDataGrid}
                onDelete={deleteClick}
                columns={columns}
                allowHeaderFilting={false}
                allowUpdating={true}
                gridProps={{
                    dataSource: diaryDataSource,
                    dataGridRef: dataGridInnerRef,
                    onFocusedCellChanging: onFocusedCellChanging
                }}
                detailOnClick={detailOnClick}
            />
        </Paper>
    );
}

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(DiaryGrid)));
